import { QueryClient } from '@tanstack/react-query';

/**
 * Cache configuration constants (in milliseconds)
 *
 * These values define how long data is considered fresh (staleTime)
 * and how long it stays in cache before garbage collection (gcTime)
 */
export const CACHE_CONFIG = {
  /**
   * User data cache: 5 minutes
   * - Moderately stable data
   * - Updated on profile changes
   */
  USER: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10 // 10 minutes
  },

  /**
   * Mention data cache: 1 minute
   * - Frequently changing data
   * - New mentions arrive regularly
   */
  MENTIONS: {
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5 // 5 minutes
  },

  /**
   * Business data cache: 10 minutes
   * - Moderately stable data
   * - Changes are infrequent
   */
  BUSINESSES: {
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30 // 30 minutes
  },

  /**
   * Region data cache: 30 minutes
   * - Very stable data
   * - Admin-managed, rarely changes
   */
  REGIONS: {
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60 // 1 hour
  },

  /**
   * Search results cache: 30 seconds
   * - Transient data
   * - User-specific, context-dependent
   */
  SEARCH: {
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 2 // 2 minutes
  }
};

/**
 * Create and configure the React Query client
 *
 * Global defaults apply to all queries unless overridden
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default staleTime: 1 minute
      staleTime: 1000 * 60,

      // Default gcTime (garbage collection): 5 minutes
      gcTime: 1000 * 60 * 5,

      // Retry failed requests once
      retry: 1,

      // Retry delay: exponential backoff (1s, 2s)
      retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 2000),

      // Don't refetch on window focus (we have background refetch)
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is fresh
      refetchOnMount: false,

      // Refetch on reconnect (user's internet came back)
      refetchOnReconnect: true
    },
    mutations: {
      // Retry mutations once on network errors
      retry: 1,

      // Retry delay for mutations
      retryDelay: 1000
    }
  }
});

/**
 * Export a typed version of useQueryClient for better TypeScript support
 */
export { useQueryClient } from '@tanstack/react-query';
